import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchActiveCountries } from '@/shared/api/places';
import { toOptions } from '@/shared/lib/options';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { FileUploadField } from '@/shared/ui/FileUploadField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { SaveBar } from '@/shared/ui/SaveBar';
import { TextField } from '@/shared/ui/TextField';
import { PromoConditionsSection } from './PromoConditionsSection';
import { PromocodesSection } from './PromocodesSection';
import {
  EMPTY_PROMO,
  deletePromoImage,
  fetchPromo,
  promoToValues,
  savePromo,
  uploadPromoImage,
  type PromoImageField,
  type PromoValues,
} from './api';

const TITLE_MAX_LENGTH = 20;
const TITLE_TOO_LONG = `Значение «Title» должно содержать максимум ${TITLE_MAX_LENGTH} символов`;
const SELECT_WIDTH = 300;

const IMAGE_FIELDS: { field: PromoImageField; label: string; errorKey: string }[] = [
  { field: 'image', label: 'Картинка для заголовка *', errorKey: 'image_id' },
  { field: 'icon', label: 'Логотип *', errorKey: 'icon_id' },
];

const INNER_IMAGE_FIELDS: typeof IMAGE_FIELDS = [
  { field: 'logo', label: 'Логотип внутренней страницы', errorKey: 'logo_id' },
  { field: 'blank', label: 'Логотип бланка', errorKey: 'blank_id' },
];

const PromoForm = ({ id, initial }: { id?: number; initial: PromoValues }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const countries = useQuery({ queryKey: ['countries', 'active'], queryFn: fetchActiveCountries });

  const form = useEntityForm(
    initial,
    (values) => savePromo(id, values),
    (res) => {
      void qc.invalidateQueries({ queryKey: ['marketing-promo'] });
      if (!id && res.promocode?.id) navigate(`/marketing/promo/edit/${res.promocode.id}`, { replace: true });
    },
  );
  const { values, errors, setField } = form;

  const renderImage = ({ field, label, errorKey }: (typeof IMAGE_FIELDS)[number]) => (
    <FileUploadField
      key={field}
      label={label}
      files={values[field]}
      errors={errors[errorKey]}
      upload={uploadPromoImage(field)}
      remove={deletePromoImage(field)}
      onChange={(files) => setField(field, files, errorKey)}
    />
  );

  return (
    <>
      <TextField
        label="Заголовок *"
        placeholder="Title"
        value={values.title}
        errors={errors.title ?? (values.title.length > TITLE_MAX_LENGTH ? [TITLE_TOO_LONG] : undefined)}
        onChange={(v) => setField('title', v)}
      />
      <TextField
        label="Описание *"
        placeholder="Описание"
        multiline
        value={values.description}
        errors={errors.description}
        onChange={(v) => setField('description', v)}
      />
      <FormField label="Страна *" errors={errors.country_id}>
        <FilterSelect
          options={toOptions(countries.data)}
          value={values.countryId}
          isLoading={countries.isLoading}
          width={SELECT_WIDTH}
          placeholder="Выберите страну..."
          onChange={(v) => setField('countryId', v, 'country_id')}
        />
      </FormField>
      {IMAGE_FIELDS.map(renderImage)}
      <FormField label="Цвет заголовка *" errors={errors.color}>
        <input
          type="color"
          value={values.color}
          onChange={(e) => setField('color', e.target.value)}
        />
      </FormField>
      <TextField
        label="Градиент баннера внутренней страницы"
        placeholder="#000000, #adadad"
        value={values.headerGradient}
        errors={errors.header_gradient}
        onChange={(v) => setField('headerGradient', v, 'header_gradient')}
      />
      <TextField
        label="Заголовок внутренней страницы"
        placeholder="Title"
        value={values.header}
        errors={errors.header}
        onChange={(v) => setField('header', v)}
      />
      {INNER_IMAGE_FIELDS.map(renderImage)}
      <TextField
        label="Координата (x) точки, начиная с которой нужно выводить промокод на изображение"
        placeholder="Координата"
        value={values.pointX}
        errors={errors.point_x ?? errors.pointX}
        onChange={(v) => setField('pointX', v, 'point_x')}
      />
      <TextField
        label="Координата (y) точки, начиная с которой нужно выводить промокод на изображение"
        placeholder="Координата"
        value={values.pointY}
        errors={errors.point_y ?? errors.pointY}
        onChange={(v) => setField('pointY', v, 'point_y')}
      />
      <SaveBar isSaving={form.isSaving} saved={form.saved} errors={errors} onSave={form.save} />
    </>
  );
};

export const PromoCreatePage = () => (
  <DetailLayout title="Создание промо">
    <PromoForm initial={EMPTY_PROMO} />
  </DetailLayout>
);

export const PromoEditPage = () => {
  const id = useRouteId();
  const { data, isLoading, error } = useQuery({ queryKey: ['marketing-promo-item', id], queryFn: () => fetchPromo(id) });

  return (
    <>
      <DetailLayout title="Редактирование промо" isLoading={isLoading} error={error}>
        {data && <PromoForm key={id} id={id} initial={promoToValues(data)} />}
      </DetailLayout>
      {data && (
        <section className="content" style={{ paddingTop: 0 }}>
          <PromocodesSection promoId={id} />
          <PromoConditionsSection promoId={id} />
        </section>
      )}
    </>
  );
};
